import { useInView } from "react-intersection-observer";
import { Text } from "@croquiscom-pvt/zds";
import { vars } from "@croquiscom-pvt/zds/tokens";
import styled from "@emotion/styled";

export interface ProductSwatchItem {
  id: string;
  imageUrl: string;
  productUrl: string;
  deeplinkUrl: string;
  isSelected: boolean;
  isDimmed: boolean;
  statusText?: {
    text?: string;
    color: {
      normal: string;
      dark?: string;
    };
    html?: string | null;
  };
  ubl?: {
    object: {
      section: string;
      type: string;
      id: string;
      idx: number;
      url: string | null;
    };
    server_log: string;
  };
}

export interface ProductSwatchCardProps extends ProductSwatchItem {
  className?: string;
  position: number;
  onClick?: (item: ProductSwatchItem, position: number) => void;
  onImpression?: (item: ProductSwatchItem, position: number) => void;
}

export const ProductSwatchCard = ({ className, position, onClick, onImpression, ...item }: ProductSwatchCardProps) => {
  const { ref } = useInView({
    triggerOnce: true,
    onChange: (inView) => {
      if (inView) {
        onImpression?.(item, position);
      }
    },
  });

  const handleItemClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onClick?.(item, position);
  };

  return (
    <Container className={className} data-selected={item.isSelected} data-dimmed={item.isDimmed}>
      <a ref={ref} href={item.productUrl} onClick={handleItemClick}>
        <ThumbnailImage src={item.imageUrl} alt={item.id} />

        {item.statusText?.text && (
          <StatusText as="span" variant='Caption_12_Medium' color='white' overrideColor={item.statusText?.color.normal}>
            {item.statusText.text}
          </StatusText>
        )}
      </a>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 48px;
  height: 58px;
  border-radius: 2px;
  overflow: hidden;

  &:after {
    position: absolute;
    display: block;
    content: '';
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background: ${vars.color.thumbnailDimmed};
    z-index: 1;
    pointer-events: none;
  }

  &[data-selected="true"] {
    border: 1px solid ${vars.color.borderStrong};
  }

  &[data-dimmed="true"]:after {
    background: ${vars.color.backgroundDimmed};
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  aspect-ratio: 24/29;
  object-fit: cover;
  object-position: center;
`;

const StatusText = styled(Text)<{ overrideColor?: string }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: pre-wrap;
  color: ${({ overrideColor }) => overrideColor};
  transform: translate(-50%, -50%);
  z-index: 2;
`;
